import type { SupplierOperationKind } from '@tandemone/types'

export type OperationFamily =
  | 'machining'
  | 'fabrication'
  | 'joining'
  | 'electronics'
  | 'assembly'
  | 'materials'

export type OperationFamilyInfo = {
  family: OperationFamily
  label: string
  className: string
}

const FAMILY_LABELS: Record<OperationFamily, string> = {
  machining: 'Machining & tooling',
  fabrication: 'Fabrication & forming',
  joining: 'Joining & surface',
  electronics: 'Electronics',
  assembly: 'Assembly & OEM',
  materials: 'Materials & metrology'
}

const FAMILY_CLASSES: Record<OperationFamily, string> = {
  machining:
    'bg-indigo-100 text-indigo-900 border-transparent dark:bg-indigo-950 dark:text-indigo-200',
  fabrication:
    'bg-sky-100 text-sky-900 border-transparent dark:bg-sky-950 dark:text-sky-200',
  joining:
    'bg-rose-100 text-rose-900 border-transparent dark:bg-rose-950 dark:text-rose-200',
  electronics:
    'bg-teal-100 text-teal-900 border-transparent dark:bg-teal-950 dark:text-teal-200',
  assembly:
    'bg-orange-100 text-orange-900 border-transparent dark:bg-orange-950 dark:text-orange-200',
  materials:
    'bg-stone-100 text-stone-900 border-transparent dark:bg-stone-800 dark:text-stone-200'
}

const FAMILY_BY_CAPABILITY: Record<SupplierOperationKind, OperationFamily> = {
  machining: 'machining',
  die_design_and_fabrication: 'machining',
  tooling_components_fixtures_gaging: 'machining',
  sheet_metal_fabrication: 'fabrication',
  stamping: 'fabrication',
  aluminum_extrusions: 'fabrication',
  cutting_and_frame_assembly: 'fabrication',
  castings: 'fabrication',
  plastic_injection_molding: 'fabrication',
  additive_manufacturing: 'fabrication',
  brazing: 'joining',
  ndt_testing_inspection_welding: 'joining',
  heat_treat_and_annealing: 'joining',
  plating_painting_coating_finishing: 'joining',
  printed_circuit_boards: 'electronics',
  electrical_component_assembly: 'electronics',
  wire_and_ribbon_processing: 'electronics',
  original_equipment_manufacturer: 'assembly',
  industrial_machinery_and_equipment: 'assembly',
  commercial_off_shelf_cots: 'assembly',
  raw_material_bar_stock: 'materials',
  metrology_calibration_cmm_gages: 'materials',
  /** "none of the above" should never be displayed as a badge, but the
   * type system requires a value. bucket it with materials so the map
   * stays exhaustive.
   */
  none_of_the_above: 'materials'
}

export function getOperationFamily(
  operation: SupplierOperationKind
): OperationFamilyInfo {
  const family = FAMILY_BY_CAPABILITY[operation]
  return {
    family,
    label: FAMILY_LABELS[family],
    className: FAMILY_CLASSES[family]
  }
}

export const OPERATION_FAMILY_LABELS = FAMILY_LABELS

export const OPERATION_FAMILY_ORDER: ReadonlyArray<OperationFamily> = [
  'machining',
  'fabrication',
  'joining',
  'electronics',
  'assembly',
  'materials'
]
